import React, { useState, useEffect } from 'react';
import { Modal, Typography, Spin, App, Space, Card, Button, Input, Row, Col } from 'antd';
import { salesService } from '../services/api';
import { v4 as uuidv4 } from 'uuid';

const { Title, Text } = Typography;

const FinancialRefundModal = ({ visible, onClose, saleId, cashRegisterId, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [prepareData, setPrepareData] = useState(null);
    const [selectedPayments, setSelectedPayments] = useState({});
    const [actionLoading, setActionLoading] = useState(false);
    const { message: messageApi } = App.useApp();

    // Загрузка данных prepare
    const loadPrepareData = async () => {
        setLoading(true);
        try {
            const response = await salesService.getRefundPrepare(saleId);
            if (response.status === 'success') {
                setPrepareData(response.data);
                // Инициализируем выбранные оплаты нулями
                const initialPayments = {};
                response.data.available_payments.forEach(payment => {
                    initialPayments[payment.payment_method_id] = '';
                });
                setSelectedPayments(initialPayments);
            } else {
                messageApi.error(response.message || 'Ошибка при получении данных для возврата');
            }
        } catch (error) {
            console.error('Ошибка загрузки данных prepare:', error);
            messageApi.error('Не удалось загрузить данные для возврата');
        } finally {
            setLoading(false);
        }
    };

    // Загрузка данных при открытии модала
    useEffect(() => {
        if (visible && saleId) {
            loadPrepareData();
        }
    }, [visible, saleId]);

    // Сброс данных при закрытии модала
    const handleModalClose = () => {
        setPrepareData(null);
        setSelectedPayments({});
        onClose();
    };

    // Форматирование суммы
    const formatAmount = (amount) => {
        if (!amount) return '0.00';
        return new Intl.NumberFormat('ru-RU', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    // Создание финансового возврата
    const handleCreateRefund = async () => {
        const returnPayments = Object.entries(selectedPayments)
            .filter(([_, amount]) => amount && parseFloat(amount) > 0)
            .map(([paymentMethodId, amount]) => {
                const payment = prepareData.available_payments.find(p => p.payment_method_id.toString() === paymentMethodId);
                return {
                    type: payment.payment_type,
                    payment_method_id: parseInt(paymentMethodId),
                    sum: parseFloat(amount),
                    payment_details: null
                };
            });

        if (returnPayments.length === 0) {
            messageApi.error('Укажите сумму для возврата');
            return;
        }

        const refundData = {
            sale_id: saleId,
            items: [],
            payments: returnPayments,
            cash_register_id: cashRegisterId
        };

        const correlationKey = uuidv4();
        const idempotencyKey = uuidv4();

        setActionLoading(true);
        try {
            const response = await salesService.createRefund(refundData, correlationKey, idempotencyKey);
            if (response.status === 'success') {
                messageApi.success('Финансовый возврат успешно создан');
                if (onSuccess) onSuccess();
                handleModalClose();
            } else {
                messageApi.error(response.message || 'Ошибка при создании возврата');
            }
        } catch (error) {
            console.error('Ошибка при создании возврата:', error);
            messageApi.error('Не удалось создать возврат');
        } finally {
            setActionLoading(false);
        }
    };

    // Функция расчета общей суммы введенных оплат
    const calculateSelectedPaymentsTotal = () => {
        return Object.values(selectedPayments).reduce((total, amount) => {
            return total + (parseFloat(amount) || 0);
        }, 0);
    };

    // Функция обработки изменения суммы оплаты
    const handlePaymentInputChange = (paymentMethodId, value) => {
        const numValue = parseFloat(value) || 0;
        const payment = prepareData.available_payments.find(p => p.payment_method_id.toString() === paymentMethodId);

        // Проверка лимита
        if (numValue > payment.available_amount) {
            value = payment.available_amount.toString();
        }

        setSelectedPayments(prev => ({
            ...prev,
            [paymentMethodId]: value
        }));
    };

    // Рендер содержимого модала
    const renderContent = () => {
        if (loading) {
            return (
                <Space
                    direction="vertical"
                    size="large"
                    style={{ width: '100%', textAlign: 'center', padding: '40px 0' }}
                >
                    <Spin size="large" />
                </Space>
            );
        }

        if (!prepareData) {
            return (
                <Space
                    direction="vertical"
                    size="large"
                    style={{ width: '100%', textAlign: 'center', padding: '40px 0' }}
                >
                    <Text type="secondary">Нет данных для возврата</Text>
                </Space>
            );
        }

        const paymentsTotal = calculateSelectedPaymentsTotal();

        return (
            <div style={{ height: '400px', overflowY: 'auto' }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        {prepareData.available_payments.map(payment => (
                            <Card key={payment.payment_method_id} size="small">
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <Row justify="space-between" align="middle">
                                        <Text strong>{payment.payment_method_name}</Text>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            Макс. {formatAmount(payment.available_amount)} ₸
                                        </Text>
                                    </Row>
                                    <Input
                                        size="large"
                                        placeholder="0.00"
                                        value={selectedPayments[payment.payment_method_id] || ''}
                                        onChange={(e) => handlePaymentInputChange(payment.payment_method_id.toString(), e.target.value)}
                                        suffix="₸"
                                        style={{ fontSize: '18px', textAlign: 'center' }}
                                    />
                                </Space>
                            </Card>
                        ))}
                    </Space>
                </Space>
            </div>
        );
    };

    return (
        <Modal
            title="Финансовый возврат"
            open={visible}
            onCancel={handleModalClose}
            width={400}
            centered
            footer={
                <Button
                    type="primary"
                    size="large"
                    onClick={handleCreateRefund}
                    loading={actionLoading}
                    style={{
                        width: '100%'
                    }}
                >
                    Создать возврат на {formatAmount(calculateSelectedPaymentsTotal())} ₸
                </Button>
            }
        >
            {renderContent()}
        </Modal>
    );
};

export default FinancialRefundModal;